import { useCallback, useState } from 'react';
import type { HistoryEntry, SavedVersion } from '../types/history';

const uid = () => `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 7)}`;

/** Timeline (newest first) and named saved versions for every model in the build, tagged by part. */
export function useProjectHistory() {
  const [entries, setEntries] = useState<HistoryEntry[]>([]);
  const [versions, setVersions] = useState<SavedVersion[]>([]);

  const record = useCallback((entry: Omit<HistoryEntry, 'id' | 'time'>) => {
    setEntries((prev) => [{ ...entry, id: uid(), time: Date.now() }, ...prev]);
  }, []);

  /** Drops every step of the same model newer than `id`, making it that model's current state. */
  const truncateTo = useCallback((id: string) => {
    setEntries((prev) => {
      const idx = prev.findIndex((e) => e.id === id);
      if (idx < 0) return prev;
      const partId = prev[idx].partId;
      return prev.filter((e, i) => e.partId !== partId || i >= idx);
    });
  }, []);

  const saveVersion = useCallback(
    (source: {title: string;time: number;thumbnail: string;snapshot: HistoryEntry['snapshot'];partId: string;}) => {
      setVersions((prev) => {
        const count = prev.filter((v) => v.partId === source.partId).length;
        return [
        {
          id: uid(),
          partId: source.partId,
          name: `Version ${count + 1}`,
          title: source.title,
          time: Date.now(),
          sourceTime: source.time,
          thumbnail: source.thumbnail,
          snapshot: source.snapshot
        },
        ...prev];

      });
    },
    []
  );

  return { entries, versions, record, truncateTo, saveVersion };
}
